import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";

/** Nomes vindos do billing-services (TemporaryReleaseIneligibility); não traduzir. */
export type TemporaryReleaseReason =
  | "CLIENT_NOT_BLOCKED"
  | "NO_OPEN_INVOICE"
  | "MORE_THAN_ONE_OPEN_INVOICE"
  | "INVOICE_ALREADY_USED"
  | "INVALID_PROOF_FILE";

export interface TemporaryReleaseEligibility {
  eligible: boolean;
  reason?: TemporaryReleaseReason;
  invoiceId?: number;
}

export interface TemporaryReleaseResult {
  invoiceId: number;
  expiresAt: string;
}

export const TEMPORARY_RELEASE_MAX_FILE_BYTES = 5 * 1024 * 1024;
export const TEMPORARY_RELEASE_ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const REASON_MESSAGES: { [reason in TemporaryReleaseReason]: string } = {
  CLIENT_NOT_BLOCKED: "Não há bloqueio ativo na sua conta.",
  NO_OPEN_INVOICE: "Não encontramos fatura em aberto para a sua conta.",
  MORE_THAN_ONE_OPEN_INVOICE:
    "A liberação por comprovante só está disponível quando há uma única fatura em aberto. " +
    "Regularize as demais ou fale com nosso atendimento.",
  INVOICE_ALREADY_USED:
    "Já recebemos um comprovante para esta fatura e não é possível solicitar uma nova liberação. " +
    "Se você já realizou o pagamento, fale com nosso atendimento.",
  INVALID_PROOF_FILE: "Envie o comprovante em PDF, JPG ou PNG, com no máximo 5 MB.",
};

/**
 * Liberação temporária do seller bloqueado mediante envio do comprovante (XD-2207).
 * O webapp-bff repassa ao billing-services, que identifica o cliente pelo token.
 */
@Injectable()
export class TemporaryReleaseService {
  private readonly URL = `${environment.angularJSconstants.URL_SERVICO_FISCAL}/v2/liberacao-temporaria`;

  constructor(private http: HttpClient) {}

  getEligibility(): Observable<TemporaryReleaseEligibility> {
    return this.http.get<TemporaryReleaseEligibility>(`${this.URL}/elegibilidade`);
  }

  requestRelease(proof: File): Observable<TemporaryReleaseResult> {
    const body = new FormData();
    body.append("comprovante", proof, proof.name);
    return this.http.post<TemporaryReleaseResult>(this.URL, body);
  }

  /** Retorna a mensagem de erro para o seller, a partir do motivo que o billing-services devolve. */
  errorMessage(error: HttpErrorResponse): string {
    const reason = this.reasonFrom(error);
    if (reason && REASON_MESSAGES[reason]) {
      return REASON_MESSAGES[reason];
    }
    if (error && error.status === 400) {
      return REASON_MESSAGES.INVALID_PROOF_FILE;
    }
    return "Não foi possível enviar o comprovante agora. Tente novamente em alguns minutos.";
  }

  private reasonFrom(error: HttpErrorResponse): TemporaryReleaseReason | null {
    let body = error && error.error;
    if (typeof body === "string") {
      try {
        body = JSON.parse(body);
      } catch (e) {
        return null;
      }
    }
    const fields = body && body.fieldValidationErrors;
    return (fields && fields.reason) || null;
  }
}
